import axios from 'axios';
import { baseURL } from '../Constants';
import { HighlightClip } from '../models/HighlightClip';

const url = `${baseURL}/Highlights`;

export interface GameDemoStatus {
    gameId: string;
    mapName?: string;
    status: 'not_queued' | 'pending' | 'processing' | 'completed' | 'failed' | 'broken';
    errorMessage?: string;
    clipCount: number;
    queuedAt?: string;
    processedAt?: string;
    queuePosition?: number | null;
}

export interface QueueWorkerInfo {
    online: boolean;
    onlineCount: number;
    lastHeartbeat?: string | null;
    avgDemoSeconds?: number | null;
}

export interface DemoStatusResponse {
    matchId: string;
    games: GameDemoStatus[];
    worker?: QueueWorkerInfo;
}

export const getClipsByPlayer = async (steamId: string): Promise<HighlightClip[]> => {
    try {
        const response = await axios.get(`${url}/player/${steamId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getClipsByPlayerAndSeason = async (steamId: string, season: number): Promise<HighlightClip[]> => {
    try {
        const response = await axios.get(`${url}/player/${steamId}/season/${season}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getClipsByMatch = async (matchId: string): Promise<HighlightClip[]> => {
    try {
        const response = await axios.get(`${url}/match/${matchId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getClipsByGame = async (gameId: string): Promise<HighlightClip[]> => {
    try {
        const response = await axios.get(`${url}/game/${gameId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getTopClips = async (
    season?: number,
    division?: string,
    startDate?: string,
    endDate?: string,
    limit: number = 20
): Promise<HighlightClip[]> => {
    try {
        const params = new URLSearchParams();
        if (season) params.append('season', season.toString());
        if (division && division !== 'all') params.append('division', division);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        params.append('limit', limit.toString());
        const response = await axios.get(`${url}/top?${params}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getPrematchClips = async (
    matchId: string,
    season?: number,
    limit: number = 20
): Promise<HighlightClip[]> => {
    try {
        const params = new URLSearchParams();
        if (season) params.append('season', season.toString());
        params.append('limit', limit.toString());
        const response = await axios.get(`${url}/prematch/${matchId}?${params}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export interface FeaturedClipsResponse {
    label: string; // "day", "week", "season", "previousSeason"
    clips: HighlightClip[];
    /** Season the clips are actually from — differs from the one requested when it fell back. */
    season?: number;
}

export const getFeaturedClips = async (season: number): Promise<FeaturedClipsResponse> => {
    try {
        const response = await axios.get(`${url}/featured?season=${season}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getDemoStatus = async (matchId: string): Promise<DemoStatusResponse> => {
    try {
        const response = await axios.get(`${url}/status/${matchId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};
